import * as XLSX from 'xlsx';
import type { CrmContactoImportFila } from '../types/crm';

export interface CrmContactosImportParseError {
  fila: number;
  mensaje: string;
}

export interface CrmContactosImportParseResult {
  filas: CrmContactoImportFila[];
  errores: CrmContactosImportParseError[];
}

type ColumnKey = 'nombre' | 'empresa' | 'cargo' | 'telefono' | 'email' | 'origen' | 'tags';

const COLUMN_ALIASES: Record<ColumnKey, string[]> = {
  nombre: ['nombre', 'name', 'contacto', 'razon social', 'razonsocial'],
  empresa: ['empresa', 'company', 'compania', 'compañia', 'organizacion', 'organización'],
  cargo: ['cargo', 'puesto', 'titulo', 'título', 'position', 'role'],
  telefono: ['telefono', 'teléfono', 'phone', 'celular', 'movil', 'móvil', 'tel'],
  email: ['email', 'e-mail', 'correo', 'mail'],
  origen: ['origen', 'fuente', 'source', 'canal'],
  tags: ['tags', 'tag', 'etiquetas', 'etiqueta', 'labels'],
};

const TEMPLATE_ORDER: ColumnKey[] = [
  'nombre',
  'empresa',
  'cargo',
  'telefono',
  'email',
  'origen',
  'tags',
];

const stripAccents = (value: string): string =>
  value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const normalizeHeader = (value: unknown): string =>
  stripAccents(String(value ?? '').trim().toLowerCase());

const cellText = (value: unknown): string => {
  if (value == null) return '';
  return String(value).trim();
};

const resolveColumnIndices = (headers: string[]): Partial<Record<ColumnKey, number>> | null => {
  const result: Partial<Record<ColumnKey, number>> = {};
  (Object.keys(COLUMN_ALIASES) as ColumnKey[]).forEach((key) => {
    const aliases = COLUMN_ALIASES[key];
    const idx = headers.findIndex((h) => aliases.some((a) => h === a || h.includes(a)));
    if (idx >= 0) result[key] = idx;
  });
  return result.nombre != null ? result : null;
};

const rowToFila = (
  row: unknown[],
  columns: Partial<Record<ColumnKey, number>>,
  fila: number,
): CrmContactoImportFila | null => {
  const get = (key: ColumnKey): string => {
    const idx = columns[key];
    if (idx == null) return '';
    return cellText(row[idx]);
  };

  const nombre = get('nombre');
  const empresa = get('empresa');
  const cargo = get('cargo');
  const telefono = get('telefono');
  const email = get('email');
  const origen = get('origen');
  const tags = get('tags');

  if (![nombre, empresa, cargo, telefono, email, origen, tags].some((v) => v)) {
    return null;
  }

  return {
    fila,
    nombre,
    empresa: empresa || null,
    cargo: cargo || null,
    telefono: telefono || null,
    email: email || null,
    origen: origen || null,
    tags: tags || null,
  };
};

const parseMatrix = (rows: unknown[][]): CrmContactosImportParseResult => {
  if (!rows.length) {
    return { filas: [], errores: [{ fila: 0, mensaje: 'No hay datos para importar.' }] };
  }

  let headerRowIndex = -1;
  let columns: Partial<Record<ColumnKey, number>> | null = null;

  for (let r = 0; r < Math.min(rows.length, 10); r += 1) {
    const headers = (rows[r] as unknown[]).map(normalizeHeader);
    const resolved = resolveColumnIndices(headers);
    if (resolved) {
      headerRowIndex = r;
      columns = resolved;
      break;
    }
  }

  if (!columns) {
    // Sin encabezado: asumir orden de plantilla si la primera celda parece nombre
    columns = TEMPLATE_ORDER.reduce<Partial<Record<ColumnKey, number>>>((acc, key, idx) => {
      acc[key] = idx;
      return acc;
    }, {});
    headerRowIndex = -1;
  }

  const filas: CrmContactoImportFila[] = [];
  const errores: CrmContactosImportParseError[] = [];
  const start = headerRowIndex + 1;

  for (let i = start; i < rows.length; i += 1) {
    const row = rows[i] as unknown[];
    if (!row || row.every((c) => cellText(c) === '')) continue;
    const parsed = rowToFila(row, columns, i + 1);
    if (!parsed) continue;
    if (!parsed.nombre.trim()) {
      errores.push({ fila: i + 1, mensaje: 'El nombre es obligatorio.' });
      continue;
    }
    filas.push(parsed);
  }

  if (filas.length === 0 && errores.length === 0) {
    return {
      filas: [],
      errores: [
        {
          fila: 0,
          mensaje:
            'No se encontraron filas válidas. Verifique que exista la columna Nombre (o use la plantilla).',
        },
      ],
    };
  }

  return { filas, errores };
};

export const parseCrmContactosExcel = async (file: File): Promise<CrmContactosImportParseResult> => {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', cellDates: false });
  if (!workbook.SheetNames.length) {
    return { filas: [], errores: [{ fila: 0, mensaje: 'El archivo no tiene hojas.' }] };
  }

  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    defval: '',
    raw: false,
  }) as unknown[][];

  return parseMatrix(rows);
};

export const parseCrmContactosClipboard = (text: string): CrmContactosImportParseResult => {
  const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
  if (!normalized) {
    return { filas: [], errores: [{ fila: 0, mensaje: 'El portapapeles está vacío.' }] };
  }

  const lines = normalized.split('\n').filter((l) => l.trim().length > 0);
  const delimiter = lines[0].includes('\t') ? '\t' : lines[0].includes(';') ? ';' : ',';
  const rows = lines.map((line) => line.split(delimiter));
  return parseMatrix(rows);
};

export const downloadCrmContactosPlantilla = (): void => {
  const rows = [
    {
      Nombre: 'Juan Pérez',
      Empresa: 'Acme SA',
      Cargo: 'Gerente comercial',
      Telefono: '11-5555-1234',
      Email: 'juan.perez@acme.com',
      Origen: 'Web',
      Tags: 'vip,prospecto',
    },
  ];
  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Contactos');
  XLSX.writeFile(workbook, 'plantilla-contactos-crm.xlsx');
};
